import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MinusCircle, X, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import walletService from '../../services/walletService';

// Admin "Debit BMF Coins" — deduct wallet coins against an external bank
// transfer (BMF-BRD-2026-014). Every field is mandatory; the transaction type
// is fixed to DEBIT. The actual bank transfer happens outside BookMyFresh.
const CATEGORIES = [
  { value: 'BANK_TRANSFER', label: 'BANK_TRANSFER' },
  { value: 'ADJUSTMENT', label: 'ADJUSTMENT' },
];

const DebitWalletModal = ({ user, onClose, onDebited }) => {
  const [amount, setAmount] = useState('');
  const [transactionRefId, setTransactionRefId] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('BANK_TRANSFER');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // Load the user's current wallet balance so the admin can see what's available
  // before entering an amount.
  useEffect(() => {
    let alive = true;
    (async () => {
      setBalanceLoading(true);
      const res = await walletService.getAdminWalletHistory(user.id);
      if (!alive) return;
      if (res.success) setBalance(Number(res.data?.balance ?? 0));
      setBalanceLoading(false);
    })();
    return () => { alive = false; };
  }, [user.id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!parsedAmount || parsedAmount < 1) {
      toast.error('Enter a valid amount (minimum ₹1)');
      return;
    }
    if (!transactionRefId.trim()) {
      toast.error('Transaction reference id is required');
      return;
    }
    if (!description.trim()) {
      toast.error('Description is required');
      return;
    }
    setLoading(true);
    try {
      const result = await walletService.adminDebitWallet(user.id, {
        amount: parsedAmount,
        transactionRefId: transactionRefId.trim(),
        description: description.trim(),
        category,
      });
      if (result.success) {
        toast.success(`₹${parsedAmount} BMF Coins debited from ${user.name} (Ref: ${transactionRefId.trim()})`);
        onDebited?.(result.data);
        onClose();
      } else {
        toast.error(result.error || 'Failed to debit BMF Coins');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-red-600">Debit BMF Coins</h3>
            <p className="text-sm text-gray-500">Deduct BMF Coins from <span className="font-medium text-gray-700">{user.name}</span>'s account</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4 text-sm text-amber-700">
          These points will be deducted from the user's BMF Coins balance. This action cannot be undone.
        </div>

        {/* Available wallet balance — read-only, shown before the amount input */}
        <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 mb-4">
          <span className="flex items-center gap-2 text-sm text-gray-600">
            <Wallet className="w-4 h-4 text-gray-500" />
            Available Balance
          </span>
          <span className="text-base font-bold text-gray-800">
            {balanceLoading ? '…' : `₹${Number(balance ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹) *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">₹</span>
              <input
                type="number"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full pl-8 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference Id *</label>
            <input
              type="text"
              value={transactionRefId}
              onChange={(e) => setTransactionRefId(e.target.value)}
              placeholder="Enter transaction reference id"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Enter the bank transfer / payment reference id</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="Enter description"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 resize-y"
              required
            />
            <p className="text-xs text-gray-400 mt-1">Provide reason or description for debiting coins</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              required
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
            <input
              type="text"
              value="DEBIT (Deduct from wallet)"
              disabled
              readOnly
              className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
            />
            <p className="text-xs text-gray-400 mt-1">Type is fixed as DEBIT for deduction</p>
          </div>

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !amount || !transactionRefId || !description}
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <MinusCircle className="w-4 h-4" />}
              Confirm Debit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

DebitWalletModal.propTypes = {
  user: PropTypes.shape({ id: PropTypes.number, name: PropTypes.string }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDebited: PropTypes.func,
};

export default DebitWalletModal;
